import { useState, type JSX } from 'react';
import {
  ARCHIVE_PROVIDERS,
  ARCHIVE_PROVIDER_LABEL_KEYS,
  readArchiveSettings,
  type ArchiveProvider,
  type Camera,
  type Recorder,
} from '@netdeck/contracts';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { useUpdateCamera, useUpdateRecorder } from '../video/api';
import { HelpNote } from '../../shared/ui/HelpNote';

/** Настройки архива камеры (§11, фаза 8). */
export function ArchiveSettings({
  camera,
  canWrite,
}: {
  camera: Camera;
  canWrite: boolean;
}): JSX.Element {
  const update = useUpdateCamera(camera.id);

  return (
    <ArchiveSettingsForm
      idPrefix={camera.id}
      provider={camera.archiveProvider}
      config={camera.archiveConfig}
      canWrite={canWrite}
      pending={update.isPending}
      error={update.error}
      onSave={(archiveProvider, archiveConfig) => update.mutate({ archiveProvider, archiveConfig })}
    />
  );
}

/**
 * Настройки архива видеорегистратора (ADR 037): одни на все каналы.
 *
 * Поля канала и дорожки скрыты — их каждому каналу проставляет сервер
 * своим номером. Показать их здесь значило бы предложить вписать номер,
 * который всё равно перепишут, и в одном поле на шестнадцать каналов сразу.
 */
export function RecorderArchiveSettings({
  recorder,
  canWrite,
}: {
  recorder: Recorder;
  canWrite: boolean;
}): JSX.Element {
  const update = useUpdateRecorder(recorder.id);

  return (
    <ArchiveSettingsForm
      idPrefix={recorder.id}
      provider={recorder.archiveProvider}
      config={recorder.archiveConfig}
      canWrite={canWrite}
      perChannel
      pending={update.isPending}
      error={update.error}
      onSave={(archiveProvider, archiveConfig) => update.mutate({ archiveProvider, archiveConfig })}
    />
  );
}

/**
 * Форма настроек архива.
 *
 * Показываются только поля выбранного способа доступа: у Hikvision это
 * дорожка, у Dahua — канал и часовой пояс камеры, у RTSP-повтора — шаблон
 * адреса. Общая анкета из всех полей сразу заставляла бы гадать, какие
 * из них относятся к делу, — а половина из них к делу не относится никогда.
 *
 * Умолчания живут в контрактах и подставляются там же: пустое поле здесь
 * означает «как обычно», а не «ничего».
 */
function ArchiveSettingsForm({
  idPrefix,
  provider: savedProvider,
  config,
  canWrite,
  perChannel = false,
  pending,
  error,
  onSave,
}: {
  idPrefix: string;
  provider: ArchiveProvider | null;
  config: Record<string, unknown>;
  canWrite: boolean;
  /** Номер канала и дорожку проставляет сервер — у регистратора (ADR 037). */
  perChannel?: boolean;
  pending: boolean;
  error: Error | null;
  onSave: (provider: ArchiveProvider | null, config: Record<string, unknown>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const saved = readArchiveSettings(config);

  const [provider, setProvider] = useState<ArchiveProvider | ''>(savedProvider ?? '');
  const [httpPort, setHttpPort] = useState(String(saved.httpPort));
  const [scheme, setScheme] = useState(saved.scheme);
  const [channel, setChannel] = useState(String(saved.channel));
  const [track, setTrack] = useState(String(saved.track));
  const [tzOffset, setTzOffset] = useState(String(saved.tzOffsetMinutes));
  const [speedParam, setSpeedParam] = useState(saved.speedParam ?? '');
  const [urlTemplate, setUrlTemplate] = useState(saved.urlTemplate ?? '');
  const [timeFormat, setTimeFormat] = useState(saved.timeFormat);

  const submit = (): void => {
    onSave(provider === '' ? null : provider, {
      httpPort: Number(httpPort) || 80,
      scheme,
      ...(perChannel ? {} : { channel: Number(channel) || 1, track: Number(track) || 101 }),
      tzOffsetMinutes: Number(tzOffset) || 0,
      ...(speedParam.trim().length === 0 ? {} : { speedParam: speedParam.trim() }),
      ...(urlTemplate.trim().length === 0 ? {} : { urlTemplate: urlTemplate.trim() }),
      timeFormat,
    });
  };

  return (
    <form
      className="grid grid-cols-[86px_1fr] items-start gap-x-3 gap-y-2 border-t border-line pt-2.5 text-sm"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label className="text-dim" htmlFor={`arch-provider-${idPrefix}`}>
        {t('nav.archive')}
      </label>
      <div className="flex flex-col gap-1">
        <select
          id={`arch-provider-${idPrefix}`}
          value={provider}
          disabled={!canWrite}
          onChange={(event) => setProvider(event.target.value as ArchiveProvider | '')}
          className="h-7 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
        >
          <option value="">{t('archive.settings.notConfigured')}</option>
          {ARCHIVE_PROVIDERS.map((value) => (
            <option key={value} value={value}>
              {t(ARCHIVE_PROVIDER_LABEL_KEYS[value])}
            </option>
          ))}
        </select>
        <div className="flex items-start gap-1 text-[11px] leading-snug text-dim">
          <span>{t('archive.settings.intro')}</span>
          <HelpNote topic="archiveProvider" anchor="archiveProvider" />
        </div>
        {perChannel && provider !== '' ? (
          <p className="text-[11px] leading-snug text-dim">{t('video.recorder.archiveHint')}</p>
        ) : null}
      </div>

      {provider === '' ? null : (
        <>
          {provider === 'rtsp_replay' ? null : (
            <>
              <label className="text-dim" htmlFor={`arch-port-${idPrefix}`}>
                {t('archive.settings.httpPort')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id={`arch-port-${idPrefix}`}
                  value={httpPort}
                  disabled={!canWrite}
                  inputMode="numeric"
                  onChange={(event) => setHttpPort(event.target.value)}
                  className="h-7 w-20 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
                />
                <select
                  value={scheme}
                  disabled={!canWrite}
                  aria-label={t('archive.settings.schemeAria')}
                  onChange={(event) => setScheme(event.target.value as 'http' | 'https')}
                  className="h-7 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
                >
                  <option value="http">http</option>
                  <option value="https">https</option>
                </select>
                <span className="text-[11px] text-dim">{t('archive.settings.portHint')}</span>
              </div>
            </>
          )}

          {provider === 'hikvision_isapi' && !perChannel ? (
            <>
              <label className="text-dim" htmlFor={`arch-track-${idPrefix}`}>
                {t('archive.settings.track')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id={`arch-track-${idPrefix}`}
                  value={track}
                  disabled={!canWrite}
                  inputMode="numeric"
                  onChange={(event) => setTrack(event.target.value)}
                  className="h-7 w-20 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
                />
                <span className="text-[11px] text-dim">{t('archive.settings.trackHint')}</span>
              </div>
            </>
          ) : null}

          {provider === 'dahua_cgi' && !perChannel ? (
            <>
              <label className="text-dim" htmlFor={`arch-channel-${idPrefix}`}>
                {t('archive.settings.channel')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id={`arch-channel-${idPrefix}`}
                  value={channel}
                  disabled={!canWrite}
                  inputMode="numeric"
                  onChange={(event) => setChannel(event.target.value)}
                  className="h-7 w-20 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
                />
                <span className="text-[11px] text-dim">{t('archive.settings.channelHint')}</span>
              </div>
            </>
          ) : null}

          {provider === 'rtsp_replay' ? (
            <>
              <label className="text-dim" htmlFor={`arch-template-${idPrefix}`}>
                {t('archive.settings.template')}
              </label>
              <div className="flex flex-col gap-1">
                <input
                  id={`arch-template-${idPrefix}`}
                  value={urlTemplate}
                  disabled={!canWrite}
                  placeholder="{path}?starttime={from}&endtime={to}"
                  onChange={(event) => setUrlTemplate(event.target.value)}
                  className="h-7 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
                />
                <p className="text-[11px] leading-snug text-dim">
                  {t('archive.settings.templateHint')}
                </p>
              </div>

              <label className="text-dim" htmlFor={`arch-format-${idPrefix}`}>
                {t('archive.settings.timeFormat')}
              </label>
              <select
                id={`arch-format-${idPrefix}`}
                value={timeFormat}
                disabled={!canWrite}
                onChange={(event) => setTimeFormat(event.target.value as typeof timeFormat)}
                className="h-7 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
              >
                <option value="iso-basic">20260823T100000Z</option>
                <option value="dahua">2026_08_23_10_00_00</option>
                <option value="unix">{t('archive.settings.epochSeconds')}</option>
              </select>
            </>
          ) : null}

          {provider === 'hikvision_isapi' ? null : (
            <>
              <label className="text-dim" htmlFor={`arch-tz-${idPrefix}`}>
                {t('archive.settings.cameraClock')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id={`arch-tz-${idPrefix}`}
                  value={tzOffset}
                  disabled={!canWrite}
                  inputMode="numeric"
                  onChange={(event) => setTzOffset(event.target.value)}
                  className="h-7 w-20 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
                />
                <span className="text-[11px] leading-snug text-dim">
                  {t('archive.settings.clockHint')}
                </span>
              </div>
            </>
          )}

          <label className="text-dim" htmlFor={`arch-speed-${idPrefix}`}>
            {t('archive.controls.speed')}
          </label>
          <div className="flex items-center gap-2">
            <input
              id={`arch-speed-${idPrefix}`}
              value={speedParam}
              disabled={!canWrite}
              placeholder={t('archive.settings.none')}
              onChange={(event) => setSpeedParam(event.target.value)}
              className="h-7 w-28 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
            />
            <span className="text-[11px] leading-snug text-dim">
              {t('archive.settings.speedHint')}
            </span>
          </div>
        </>
      )}

      {error === null ? null : (
        <p className="col-span-2 text-[11px] text-down-fg">{errorText(error)}</p>
      )}

      {canWrite ? (
        <Button
          size="sm"
          variant="secondary"
          icon="check"
          type="submit"
          className="col-start-2"
          disabled={pending}
        >
          {t('archive.settings.save')}
        </Button>
      ) : null}
    </form>
  );
}
